import { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { UseQueryResult } from 'react-query'

import { formatDate, timeAgo } from 'lib/date'
import { Params } from 'api/fetch'

import Table, { getExpanderCell } from 'backoffice/components/table/Table'
import Card from 'backoffice/components/GpsPetitions/Card'
import ListMenuButton from 'backoffice/components/GpsPetitions/ListMenuButton'
import { useGetGpsPetitionsQuery } from './useGpsPetitionsQuery'

import { GpsSupplyRequest, SupplyRequestReviewStatus, TableData } from './types/types'
import { ROUTES } from 'lib/constants'
import { copyToClipboard } from 'lib/utils'

import { ReactComponent as ClockIcon } from 'images/icons/clock.svg'
import { ReactComponent as BoxIcon } from 'images/icons/box.svg'
import { ReactComponent as CheckIcon } from 'images/icons/check-circle.svg'
import { ReactComponent as TimesIcon } from 'images/icons/times-circle.svg'
import { ReactComponent as FlagIcon } from 'images/icons/flag-circle.svg'

import styles from './List.module.scss'

const PAGINATION_OPTIONS = [10, 50, 100, 1000]
const DEFAULT_LIMIT = PAGINATION_OPTIONS[0]

interface GpsPetitionsListProps {
  petitionsQuery?: UseQueryResult<TableData, unknown>
  setQueryParams?: (params: Params | ((prev: Params) => Params)) => void
}

const GpsPetitionsList = ({
  petitionsQuery: externalQuery,
  setQueryParams: externalSetQueryParams,
}: GpsPetitionsListProps) => {
  const { addToast } = useToasts()

  const internalQuery = useGetGpsPetitionsQuery({
    page: '1',
    limit: DEFAULT_LIMIT.toString(),
  })

  const petitionsQuery = externalQuery || internalQuery.petitionsQuery
  const setQueryParams = externalSetQueryParams || internalQuery.setQueryParams

  const handleCopy = useCallback(
    (text: string) => {
      copyToClipboard(text)
      addToast(`Text Copied!`, {
        appearance: 'success',
        autoDismiss: true,
      })
    },
    [addToast],
  )

  const columns = useMemo(
    () => [
      getExpanderCell(),
      {
        Header: 'Req. ID',
        accessor: 'id',
        className: styles.idField,
        isExpandable: true,
        Cell: ({ cell: { value: id } }: any) => {
          return (
            <div
              className={styles.cellContentWrapper}
              aria-label={id}
              data-cooltipz-dir="top"
              onClick={() => handleCopy(id)}
            >
              {id.substring(0, 8)}...
            </div>
          )
        },
      },
      {
        Header: 'Drop',
        id: 'dropId',
        className: styles.eventIdField,
        isExpandable: true,
        Cell: ({ row: { original } }: any) => {
          const { drop } = original as GpsSupplyRequest
          const dropName = drop?.name || 'Unknown Drop'
          return (
            <div
              className={styles.cellContentWrapper}
              aria-label={`#${drop.id} - ${dropName}`}
              data-cooltipz-dir="top"
              data-cooltipz-size="large"
            >
              <Link to={`${ROUTES.event.path}${drop.id}`} className={styles.eventName}>
                #{drop.id} - {dropName}
              </Link>
            </div>
          )
        },
      },
      {
        Header: 'Date Created',
        accessor: 'createdAt',
        isExpandable: true,
        Cell: ({ cell }: any) => {
          return (
            <div
              className={cn(styles.createdAt, styles.cellContentWrapper)}
              aria-label={formatDate(new Date(cell.value), 'd-MMM-yyyy HH:mm')}
              data-cooltipz-dir="top"
            >
              <ClockIcon className={styles.icon} />
              <span>{timeAgo(cell.value)}</span>
            </div>
          )
        },
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        isExpandable: true,
        Cell: ({ cell }: any) => {
          return (
            <>
              <BoxIcon className={styles.icon} />
              <span>{cell.value}</span>
            </>
          )
        },
      },
      {
        Header: 'Status',
        accessor: 'reviewStatus',
        className: styles.statusCell,
        isExpandable: true,
        Cell: ({ cell }: any) => (
          <>
            {cell.value === SupplyRequestReviewStatus.APPROVED && (
              <span aria-label="Approved" data-cooltipz-dir="top">
                <CheckIcon />
              </span>
            )}
            {cell.value === SupplyRequestReviewStatus.REJECTED && (
              <span aria-label="Rejected" data-cooltipz-dir="top">
                <TimesIcon />
              </span>
            )}
            {cell.value === SupplyRequestReviewStatus.PENDING && (
              <span aria-label="Pending" data-cooltipz-dir="top">
                <FlagIcon />
              </span>
            )}
          </>
        ),
      },
      {
        id: 'edit',
        className: styles.editCell,
        Cell: ({ row: { original } }: any) => {
          const petition = original as GpsSupplyRequest
          return (
            <>
              {petition.reviewStatus === SupplyRequestReviewStatus.PENDING && (
                <ListMenuButton petition={petition} className={styles.cellContentWrapper} />
              )}
            </>
          )
        },
      },
    ],
    [handleCopy],
  )

  const renderCard = useCallback(
    ({ row }: { row: { original: GpsSupplyRequest } }) => <Card petition={row.original} />,
    [],
  )

  return (
    <>
      <Table
        columns={columns}
        query={petitionsQuery}
        setQueryParams={setQueryParams}
        renderRowSubComponent={renderCard}
        className={styles.table}
        resultsPerPage={PAGINATION_OPTIONS}
      />
    </>
  )
}

export default GpsPetitionsList
